import { CreativeLightbox } from "@/components/chat/CreativeLightbox";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { CreativeUploadPreviewsProps } from "@/types/creative-library";

/** Local previews are released on replacement/close; the saved files still live on the server. */
export function CreativeUploadPreviews({
  files,
  titles,
  disabled,
  onTitle,
}: CreativeUploadPreviewsProps) {
  const { t } = useTranslation("app");
  const id = useId();
  const [urls, setUrls] = useState<string[]>([]);
  const [preview, setPreview] = useState<number | null>(null);
  useEffect(() => {
    const next = files.map((file) => URL.createObjectURL(file));
    setUrls(next);
    setPreview(null);
    return () => next.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);
  if (!files.length) return null;
  return (
    <>
      <div
        className="grid grid-cols-2 gap-3 sm:grid-cols-3"
        data-testid="creative-upload-previews"
      >
        {files.map((file, index) => (
          <div
            key={`${file.name}:${index}`}
            className="flex min-w-0 flex-col gap-2 rounded-xl border border-border bg-background p-2"
          >
            {urls[index] && (
              <button
                type="button"
                className="cursor-zoom-in rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setPreview(index)}
                aria-label={file.name}
              >
                <img
                  src={urls[index]}
                  alt={file.name}
                  className="h-28 w-full rounded-lg object-contain"
                />
              </button>
            )}
            {files.length > 1 && (
              <>
                <Label className="text-xs" htmlFor={`${id}-${index}`}>
                  {t("creativeLibrary.title")}
                </Label>
                <Input
                  id={`${id}-${index}`}
                  value={titles[index] ?? file.name}
                  disabled={disabled}
                  onChange={(event) => onTitle(index, event.target.value)}
                  data-testid={`creative-upload-image-title-${index}`}
                />
              </>
            )}
          </div>
        ))}
      </div>
      {preview !== null && (
        <CreativeLightbox
          creatives={urls.map((src, index) => ({
            src,
            mimeType: files[index]?.type,
          }))}
          index={preview}
          onIndex={setPreview}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
}
